import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Parse from "parse";

import { useGame } from "../header/GameContext";
import { ANSWERS_DATA } from "./quizData";
import { saveToSheet } from "../../api/sheet";

import ButtonOnly from "../button/buttonOnly";

const NO_MISSION_CODE = [
  "final0",
  "end_q1",
  "end_q2",
  "end_q3",
  "end_q4",
  "end_q5",
  "end_q6",
  "last_q",
]; //단서 증가 없는 스테이지 코드

const getGameUserRow = async (objectId) => {
  const query = new Parse.Query(Parse.Object.extend("spyx"));
  return await query.get(objectId);
};

const saveScore = async (playerKeyCode, score) => {
  const query = new Parse.Query(Parse.Object.extend("spyx"));
  query.equalTo("keyCode", playerKeyCode);
  const user = await query.first();
  if (!user) throw new Error("유저를 찾을 수 없음");
  user.set("score", score);
  await user.save();
};

const saveSuspectScoreClearTime = async (playerKeyCode, score, usedTime) => {
  const query = new Parse.Query(Parse.Object.extend("spyx"));
  query.equalTo("keyCode", playerKeyCode);
  const user = await query.first();
  if (!user) throw new Error("유저를 찾을 수 없음");
  user.set("clearTime", usedTime);
  user.set("suspectScore", score);
  await user.save();
};

function QuizeCheck({ answerData, onWrong }) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState("");
  const isActive = value.trim().length > 0;

  const {
    time,
    pauseTimer,
    playerKeyCode,
    isFinalQuiz,
    isLastQuiz,
    correctCount,
    setCorrectCount,
    setSuspectScore,
    increaseMissionOnce,
    TOTAL_GAME_TIME,
  } = useGame();

  const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();

  const isMissionAllowed = !NO_MISSION_CODE.includes(answerData);

  // 클리어한 퀴즈 기록 DB저장
  const trySaveClearedQuiz = async () => {
    try {
      const objectId = localStorage.getItem("gameUserId");
      const userRow = await getGameUserRow(objectId);
      userRow.addUnique("clearedQuiz", answerData);
      await userRow.save();
    } catch (e) {
      console.error("❌ 클리어 퀴즈 저장 실패", e);
    }
  };

  // 최종 추론 점수 DB저장
  const trySaveScore = async (scoreCorrectCount) => {
    if (answerData !== "end_q6") return;

    const finalQuizScore = scoreCorrectCount * 10;

    try {
      await saveScore(playerKeyCode, finalQuizScore);
    } catch (e) {
      console.error("❌ 점수 저장 실패", e);
    }

    //***** Google Sheet { *****//
    try {
      await saveToSheet({
        keyCode: playerKeyCode, //keyCode로 기존 row 찾아서 업데이트
        score: finalQuizScore,
      });
    } catch (sheetError) {
      console.error("시트 저장 실패:", sheetError.message);
    }
    //***** } Google Sheet *****//
  };

  // 범인특정퀴즈 점수 + 클리어시간 DB저장
  const trySaveSuspectScoreClearTime = async (scoreCorrectCount) => {
    if (answerData !== "last_q") return;

    const suspectQuizScore = scoreCorrectCount;

    // ✅ state 업데이트 기다리지 않고 현재 localStorage 값 직접 읽기
    const currentTime =
      Number(localStorage.getItem("GAME_TIME_REMAIN")) || time;
    const usedTime = TOTAL_GAME_TIME - currentTime;
    const minutes = Math.floor(usedTime / 60);
    const seconds = usedTime % 60;
    const escapeTime = `${minutes}분 ${seconds < 10 ? "0" : ""}${seconds}초`;

    pauseTimer(); // 타이머 정지

    try {
      await saveSuspectScoreClearTime(
        playerKeyCode,
        suspectQuizScore,
        usedTime,
      );
    } catch (e) {
      console.error("❌ 점수 저장 실패", e);
    }

    try {
      await saveToSheet({
        keyCode: playerKeyCode,
        suspectScore: suspectQuizScore,
        clearTime: escapeTime,
      });
    } catch (sheetError) {
      console.error("시트 저장 실패:", sheetError.message);
    }
  };

  // 페이지 이동('/quiz'에 맞는 state) : 최종추론
  const handleQuizCorrect = async (quiz, nextCorrectCount) => {
    navigate(quiz.next.route, {
      state: {
        answerData: quiz.next.answerData,
        DateVal: quiz.next.DateVal,
        titImgClass: quiz.next.titImgClass,
        hintWhetherIs: quiz.next.hintWhetherIs,
        hintContent: quiz.next.hintContent,
      },
    });
  };

  // 페이지 이동('/answer'에 맞는 state) : 일반퀴즈
  const handleAnswerCorrect = (quiz) => {
    navigate(quiz.next.route, {
      state: {
        DateVal: quiz.next.DateVal,
        missionKey: answerData,
        titImgClass: quiz.next.titImgClass,
        counting: quiz.next.counting,
      },
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true); // 로딩 시작

    const quiz = ANSWERS_DATA[answerData];

    if (!quiz) {
      onWrong?.();
      return;
    }

    const isCorrect = (() => {
      const input = normalize(value);
      const answers = Array.isArray(quiz.answer) ? quiz.answer : [quiz.answer];
      return answers.some((ans) => normalize(ans) === input);
    })();

    // ❌ 오답 처리
    if (!isCorrect) {
      if (isFinalQuiz(answerData)) {
        await trySaveClearedQuiz();
        await trySaveScore(correctCount);

        navigate("/wrong", {
          state: {
            nextState: {
              route: quiz.next.route,
              answerData: quiz.next.answerData,
              DateVal: quiz.next.DateVal,
              titImgClass: quiz.next.titImgClass,
              btnTxt: "다음 문제",
            },
            message: "추론이 틀린 것 같아...<br/>다음 문제를 풀어보자.",
          },
        });
      } else if (isLastQuiz(answerData)) {
        await trySaveSuspectScoreClearTime(0);
        setSuspectScore(0);

        navigate("/wrong", {
          state: {
            nextState: {
              route: quiz.next.route,
              answerData: quiz.next.answerData,
              DateVal: quiz.next.DateVal,
              titImgClass: quiz.next.titImgClass,
              btnTxt: "다음으로",
            },
            message: "추론이 틀린 것 같아...<br/>추리 점수를 확인하자.",
          },
        });
      } else {
        onWrong?.();
      }
      setIsLoading(false); // 실패 시 로딩 해제
      return;
    }

    // ✅ 정답 (다음 페이지 이동 + 클리어카운트증가 + 점수저장)
    await trySaveClearedQuiz();
    if (isFinalQuiz(answerData)) {
      const nextCorrectCount = correctCount + 1;
      setCorrectCount(nextCorrectCount);
      await trySaveScore(nextCorrectCount);
      await handleQuizCorrect(quiz);
    } else if (isLastQuiz(answerData)) {
      await trySaveSuspectScoreClearTime(20);
      setSuspectScore(20);
      handleAnswerCorrect(quiz);
    } else {
      handleAnswerCorrect(quiz);
    }

    if (isMissionAllowed) {
      increaseMissionOnce(answerData);
    }
  };

  return (
    <div className="mt24 mb40">
      <input
        className="input01"
        placeholder="정답을 입력하세요."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
      />
      <ButtonOnly
        btnValue={
          isLoading ? (
            <>
              <span className="btn-spinner white" />
            </>
          ) : (
            "입력 완료"
          )
        }
        btnClass={`inputBtn01 mt16 ${isActive ? "active" : ""}`}
        onFinish={handleSubmit}
        disabled={!isActive || isLoading}
      />
    </div>
  );
}

export default QuizeCheck;
